import { differenceInDays, getMonth, getWeek, isWithinInterval } from 'date-fns';

import type { ContentType } from '../../store/storeTypes';

/**
 * The existing cc-analytics rest APIs have a 'bug', where they return data for a day/week/month before the requested date.
 * This method should not be called by any caller, other than the ones impacted by this bug.
 * When we move to new freshly built graphql apis, we will fix this bug on the backend, and remove usage of this method.
 * https://hello.jira.atlassian.cloud/browse/CCIR-132
 *
 * @param period the period for which data is requested
 * @param fromDate the desired start date of the data
 * @param toDate  the desired end date of the data
 * @param data the data returned by the rest api, a single data point
 *
 * @returns true if the data is valid based on the period and dates, false otherwise
 */
export const _filterOutOfRangeNodesFromResponse = (
	period: string,
	fromDate: Date,
	toDate: Date,
	data: {
		date: string;
		total: number;
	},
): boolean => {
	if (!data || !data.date) {
		return false;
	}
	const dateOfData = new Date(data.date);

	// Regardless of period, if the date falls within the requested range, it is valid
	const isDateWithinInterval = isWithinInterval(dateOfData, { start: fromDate, end: toDate });
	if (isDateWithinInterval) {
		return true;
	}
	if (period === 'month') {
		const monthOfFromDate = getMonth(fromDate);
		const monthOfToDate = getMonth(toDate);
		const monthOfDataDate = getMonth(dateOfData);
		// We already checked for dates within the range, so we don't need to check that here.
		// So just check if the month of the data is the same as the month of the from or to date
		if (monthOfFromDate == monthOfDataDate || monthOfToDate == monthOfDataDate) {
			return true;
		}
		return false;
	} else if (period === 'week') {
		const weekOfFromDate = getWeek(fromDate);
		const weekOfToDate = getWeek(toDate);
		const weekOfDataDate = getWeek(dateOfData);
		// We already checked for dates within the range, so we don't need to check that here.
		// So just check if the week of the data is the same as the week of the from or to date
		if (weekOfFromDate == weekOfDataDate || weekOfToDate == weekOfDataDate) {
			return true;
		}
		return false;
	} else if (period === 'day') {
		const fromDaysDifference = differenceInDays(fromDate, dateOfData);
		const toDaysDifference = differenceInDays(toDate, dateOfData);
		// Can't be more than a day before the from date, or a day after the to date
		if (fromDaysDifference < 0 || toDaysDifference > 0) {
			return false;
		}
		return true;
	}
	return true;
};

/**
 * Some endpoints implemented the entity name as "blog," although "blogpost" is the widely adopted official name.
 * Until we fix this in the endpoints themselves, this serves as a workaround.
 *
 * @param content any content type
 * @returns the fixed "workaround" content type as "blog"
 */
export const _convertBlogpostToBlogParam = (contentType: ContentType) =>
	contentType === 'blogpost' ? 'blog' : contentType;
